import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Info, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { OrganizationAccessUser, UserRetrieve } from 'src/api/main'
import { usersRetrieveOptions } from 'src/api/main'
import { DateTimeCell } from 'src/components'
import { formatPhoneNumber } from 'src/utils'

const { Text } = Typography

type Props = {
  user: OrganizationAccessUser | UserRetrieve
  jobTitle?: string | null
  onEdit?: () => void
}

const toExternalUrl = (value: string) =>
  /^https?:\/\//i.test(value) ? value : `https://${value}`

export const UserIdentityCard = ({ user, jobTitle, onEdit }: Props) => {
  const { t } = useTranslation()

  // The workspace-access user omits profile-only fields such as
  // `about_me`/`linkedin_url`; fetch the full user to surface them.
  const { data: profile } = useQuery({
    ...usersRetrieveOptions({ path: { id: user.id } }),
    enabled: Number.isFinite(user.id) && user.id > 0
  })

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap={6}>
          <Text strong style={{ fontSize: 13 }}>
            {t('users.detail.identity.title')}
          </Text>
          <Tooltip title={t('users.detail.identity.subtitle')}>
            <Info
              size={12}
              style={{
                color: 'var(--ant-color-text-tertiary)',
                cursor: 'help'
              }}
              aria-label={t('users.detail.identity.subtitle')}
            />
          </Tooltip>
        </Flex>
      }
      extra={
        onEdit ? (
          <Button
            size="small"
            type="text"
            icon={<Pencil size={13} />}
            onClick={onEdit}
          >
            {t('users.actions.edit')}
          </Button>
        ) : null
      }
    >
      <Descriptions
        column={2}
        size="small"
        colon={false}
        styles={{ label: { width: 120 }, content: { fontSize: 12 } }}
      >
        <Descriptions.Item label={t('users.detail.identity.firstName')}>
          {user.first_name || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.lastName')}>
          {user.last_name || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.email')}>
          <Flex align="center" gap={6}>
            <Text
              copyable={{ tooltips: false }}
              style={{ fontSize: 12 }}
              ellipsis={{ tooltip: user.email }}
            >
              {user.email}
            </Text>
            <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 10 }}>
              {t('users.detail.identity.verified')}
            </Tag>
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.phone')}>
          {user.phone ? (
            <Text style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
              {formatPhoneNumber(user.phone)}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.jobTitle')}>
          {jobTitle ? (
            <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: jobTitle }}>
              {jobTitle}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.position')}>
          {user.position ? (
            <Text style={{ fontSize: 12 }}>{user.position}</Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.language')}>
          {user.language ?? <Text type="secondary">—</Text>}
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.identity.birthDate')}>
          <DateTimeCell value={profile?.birth_date} variant="date" />
        </Descriptions.Item>
        <Descriptions.Item label={t('linkedin_url')} span={2}>
          {profile?.linkedin_url ? (
            <Typography.Link
              href={toExternalUrl(profile.linkedin_url)}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                fontSize: 12,
                maxWidth: '100%',
                overflowWrap: 'anywhere'
              }}
            >
              {profile.linkedin_url}
            </Typography.Link>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('about_me')} span={2}>
          {profile?.about_me ? (
            <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {profile.about_me}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}
